/**
 * ExtensionSection Component
 *
 * Displays and manages account-level extensions for a specific type (callout, sitelink, or structured snippet).
 * Includes list of existing extensions, batch actions, and add new functionality.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { KebabMenu } from '@/components/ui/KebabMenu';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  saveAccountExtension,
  updateAccountExtensionStatus,
  type AccountExtension,
} from '@/services/api/accountExtensions';
import { toast, debug } from '@/utils';
import type { AccountExtensionType, AccountExtensionStatus } from '../types';
import {
  ACCOUNT_EXTENSION_LIMITS,
  SITELINK_DESCRIPTION_LIMIT,
  STATUS_BADGE_CONFIG,
  STRUCTURED_SNIPPET_HEADERS,
  ACCOUNT_EXTENSION_LABELS,
} from '../types';

interface ExtensionSectionProps {
  memberUuid: string;
  customerId: string;
  assetType: AccountExtensionType;
  assets: AccountExtension[];
  isLoading: boolean;
  onAssetsUpdated: () => void;
  onOptimisticChange?: (removedCount: number, statusChanges: number) => void;
}

// Extended type for displayed assets with local update tracking
type DisplayedExtension = AccountExtension & {
  localStatus?: AccountExtensionStatus;
  isRemoved?: boolean;
};

export function ExtensionSection({
  memberUuid,
  customerId,
  assetType,
  assets,
  isLoading,
  onAssetsUpdated,
  onOptimisticChange,
}: ExtensionSectionProps) {
  // State for optimistic updates
  const [localUpdates, setLocalUpdates] = useState<Map<string, { status?: AccountExtensionStatus; removed?: boolean }>>(new Map());

  // State for selected assets (batch actions)
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());

  // State for add new extension
  const [newExtensionText, setNewExtensionText] = useState('');
  const [newSitelinkUrl, setNewSitelinkUrl] = useState('');
  const [newSitelinkDesc1, setNewSitelinkDesc1] = useState('');
  const [newSitelinkDesc2, setNewSitelinkDesc2] = useState('');
  const [newSnippetHeader, setNewSnippetHeader] = useState<string>(STRUCTURED_SNIPPET_HEADERS[0]);
  const [newSnippetValues, setNewSnippetValues] = useState('');

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Confirmation dialog state
  const [pendingAction, setPendingAction] = useState<'enable' | 'pause' | 'remove' | null>(null);

  const maxLength = ACCOUNT_EXTENSION_LIMITS[assetType];

  // Reset form fields
  const resetForm = useCallback(() => {
    setNewExtensionText('');
    setNewSitelinkUrl('');
    setNewSitelinkDesc1('');
    setNewSitelinkDesc2('');
    setNewSnippetHeader(STRUCTURED_SNIPPET_HEADERS[0]);
    setNewSnippetValues('');
  }, []);

  // Reset selection when asset type changes
  useEffect(() => {
    setSelectedAssets(new Set());
    resetForm();
  }, [assetType, resetForm]);

  // Get displayed extensions with local updates applied
  const getDisplayedExtensions = useCallback((): DisplayedExtension[] => {
    return assets
      .filter((asset) => !localUpdates.get(asset.asset_id)?.removed)
      .map((asset) => ({
        ...asset,
        status: (localUpdates.get(asset.asset_id)?.status || asset.status) as AccountExtensionStatus,
      }));
  }, [assets, localUpdates]);

  const displayedExtensions = getDisplayedExtensions();

  // Check if form is valid for submission
  const isFormValid = useCallback((): boolean => {
    if (assetType === 'structured_snippet') {
      // Structured snippets require 3-10 values
      const values = newSnippetValues.split(',').map((v) => v.trim()).filter((v) => v);
      return values.length >= 3 && values.length <= 10;
    }
    if (assetType === 'sitelink') {
      return newExtensionText.trim().length > 0 && newSitelinkUrl.trim().length > 0;
    }
    return newExtensionText.trim().length > 0;
  }, [assetType, newExtensionText, newSitelinkUrl, newSnippetValues]);

  // Handle add new extension
  const handleAddExtension = async () => {
    if (assetType !== 'structured_snippet' && !newExtensionText.trim()) {
      toast.warning('Please enter extension text');
      return;
    }

    // Validate sitelink URL
    if (assetType === 'sitelink' && !newSitelinkUrl.trim()) {
      toast.warning('Please enter a URL for the sitelink');
      return;
    }

    // Validate structured snippet values (3-10 required)
    if (assetType === 'structured_snippet') {
      const snippetValues = newSnippetValues.split(',').map((v) => v.trim()).filter((v) => v);
      if (snippetValues.length < 3) {
        toast.warning('Structured snippets require at least 3 values');
        return;
      }
      if (snippetValues.length > 10) {
        toast.warning('Structured snippets allow a maximum of 10 values');
        return;
      }
    }

    setIsSaving(true);
    try {
      // Parse structured snippet values
      const values = assetType === 'structured_snippet'
        ? newSnippetValues.split(',').map((v) => v.trim()).filter((v) => v)
        : undefined;

      // For structured snippets, construct text from header and values
      const text = assetType === 'structured_snippet'
        ? `${newSnippetHeader}: ${newSnippetValues}`
        : newExtensionText.trim();

      const response = await saveAccountExtension({
        member_uuid: memberUuid,
        customer_id: customerId,
        asset_type: assetType,
        text: text,
        final_url: assetType === 'sitelink' ? newSitelinkUrl.trim() : undefined,
        description1: assetType === 'sitelink' ? newSitelinkDesc1.trim() || undefined : undefined,
        description2: assetType === 'sitelink' ? newSitelinkDesc2.trim() || undefined : undefined,
        header: assetType === 'structured_snippet' ? newSnippetHeader : undefined,
        values: values,
        trigger_data_sync: true,
      });

      if (response.success) {
        toast.success(`${assetType.replace('_', ' ')} added successfully`);
        resetForm();
        onAssetsUpdated();
      } else {
        toast.error(response.error || 'Failed to add extension');
      }
    } catch (error) {
      debug.error('Failed to add extension:', error);
      toast.error('Failed to add extension');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle batch status update
  const handleBatchStatusUpdate = async (action: 'enable' | 'pause' | 'remove') => {
    if (selectedAssets.size === 0) {
      toast.warning('Please select at least one extension');
      return;
    }

    setIsUpdatingStatus(true);
    const assetIds = Array.from(selectedAssets);

    // Optimistic update
    const newLocalUpdates = new Map(localUpdates);
    let removedCount = 0;
    assetIds.forEach((assetId) => {
      if (action === 'remove') {
        newLocalUpdates.set(assetId, { removed: true });
        removedCount++;
      } else {
        newLocalUpdates.set(assetId, { status: action === 'enable' ? 'ENABLED' : 'PAUSED' });
      }
    });
    setLocalUpdates(newLocalUpdates);
    setSelectedAssets(new Set());

    // Notify parent of optimistic change (for count updates)
    if (onOptimisticChange && removedCount > 0) {
      onOptimisticChange(removedCount, 0);
    }

    try {
      const response = await updateAccountExtensionStatus({
        member_uuid: memberUuid,
        customer_id: customerId,
        asset_ids: assetIds,
        action,
        trigger_data_sync: true,
      });

      const actionPast = action === 'enable' ? 'enabled' : action === 'pause' ? 'paused' : 'removed';

      if (response.success && response.error_count === 0) {
        // Full success - all updates succeeded
        toast.success(`${response.updated_count} extension(s) ${actionPast}`);
        onAssetsUpdated();
      } else if (response.success && response.error_count > 0) {
        // Partial success - some updates failed
        // Revert only the failed assets from optimistic updates
        const failedAssetIds = new Set(response.errors?.map(e => e.asset_id).filter(Boolean));
        // Compute reverted count outside state updater since React state updates are async
        // For remove actions, count how many failed assets we're reverting
        const revertedRemoveCount = action === 'remove' ? failedAssetIds.size : 0;
        setLocalUpdates((prev) => {
          const updated = new Map(prev);
          failedAssetIds.forEach((id) => {
            if (id) {
              updated.delete(id);
            }
          });
          return updated;
        });
        // Notify parent to restore reverted counts
        if (onOptimisticChange && revertedRemoveCount > 0) {
          onOptimisticChange(-revertedRemoveCount, 0);
        }
        toast.warning(
          `${response.updated_count} extension(s) ${actionPast}, but ${response.error_count} failed`
        );
        onAssetsUpdated();
      } else {
        // Complete failure - revert all optimistic updates
        // Use action and assetIds.length since React state updates are async
        // and localUpdates may not reflect the optimistic updates yet
        const revertedRemoveCount = action === 'remove' ? assetIds.length : 0;
        setLocalUpdates(new Map());
        // Notify parent to restore reverted counts
        if (onOptimisticChange && revertedRemoveCount > 0) {
          onOptimisticChange(-revertedRemoveCount, 0);
        }
        toast.error(response.error || 'Failed to update extensions');
      }
    } catch (error) {
      // Revert all optimistic updates on exception
      // Use action and assetIds.length since React state updates are async
      const revertedRemoveCount = action === 'remove' ? assetIds.length : 0;
      setLocalUpdates(new Map());
      // Notify parent to restore reverted counts
      if (onOptimisticChange && revertedRemoveCount > 0) {
        onOptimisticChange(-revertedRemoveCount, 0);
      }
      debug.error('Failed to update extension status:', error);
      toast.error('Failed to update extensions');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Toggle selection
  const toggleSelection = (assetId: string) => {
    const newSelection = new Set(selectedAssets);
    if (newSelection.has(assetId)) {
      newSelection.delete(assetId);
    } else {
      newSelection.add(assetId);
    }
    setSelectedAssets(newSelection);
  };

  // Select all
  const selectAll = () => {
    const allIds = displayedExtensions.map((e) => e.asset_id);
    setSelectedAssets(new Set(allIds));
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedAssets(new Set());
  };

  // Render status badge
  const renderStatusBadge = (status: AccountExtensionStatus) => {
    const config = STATUS_BADGE_CONFIG[status] || STATUS_BADGE_CONFIG.ENABLED;
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  // Render extension text (with special formatting for structured snippets)
  const renderExtensionText = (extension: DisplayedExtension) => {
    if (assetType === 'structured_snippet') {
      return (
        <div>
          <span className="font-medium text-gray-900">{extension.header}:</span>{' '}
          <span className="text-gray-600">{extension.values?.join(', ') || extension.text}</span>
        </div>
      );
    }
    if (assetType === 'sitelink' && extension.final_url) {
      return (
        <div>
          <div className="font-medium text-gray-900">{extension.text}</div>
          <div className="text-xs text-blue-600 truncate">{extension.final_url}</div>
        </div>
      );
    }
    return <span className="text-gray-900">{extension.text}</span>;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Batch Actions Bar with KebabMenu */}
      {displayedExtensions.length > 0 && (
        <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedAssets.size > 0 && selectedAssets.size === displayedExtensions.length}
              onChange={(e) => (e.target.checked ? selectAll() : deselectAll())}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              aria-label="Select all extensions"
            />
            <span className="text-sm text-gray-600">
              {selectedAssets.size > 0
                ? `${selectedAssets.size} selected`
                : `${displayedExtensions.length} extension${displayedExtensions.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-xs font-medium text-gray-500 uppercase">Status</span>
            <KebabMenu
              options={[
                {
                  label: 'Enable Selected',
                  onClick: () => setPendingAction('enable'),
                  variant: 'success',
                  disabled: selectedAssets.size === 0 || isUpdatingStatus,
                },
                {
                  label: 'Pause Selected',
                  onClick: () => setPendingAction('pause'),
                  disabled: selectedAssets.size === 0 || isUpdatingStatus,
                },
                {
                  label: 'Remove Selected',
                  onClick: () => setPendingAction('remove'),
                  variant: 'danger',
                  disabled: selectedAssets.size === 0 || isUpdatingStatus,
                },
              ]}
              ariaLabel={`Bulk actions for selected ${assetType.replace('_', ' ')}s`}
            />
          </div>
        </div>
      )}

      {/* Extensions List */}
      {displayedExtensions.length > 0 ? (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
          {displayedExtensions.map((extension) => (
            <div
              key={extension.asset_id}
              className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={selectedAssets.has(extension.asset_id)}
                onChange={() => toggleSelection(extension.asset_id)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                aria-label={`Select ${extension.text}`}
              />
              <div className="flex-1 min-w-0">
                {renderExtensionText(extension)}
              </div>
              <div className="flex-shrink-0">
                {renderStatusBadge(extension.status)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">
            No {assetType.replace('_', ' ')}s yet. Add one to get started.
          </p>
        </div>
      )}

      {/* Add New Form */}
      <div className="mt-4 p-4 border rounded-lg bg-gray-50">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Add {ACCOUNT_EXTENSION_LABELS[assetType]}
        </h4>
        <div className="space-y-3">
          {/* Text input (for callouts and sitelinks) */}
          {assetType !== 'structured_snippet' && (
            <div>
              <Input
                type="text"
                value={newExtensionText}
                onChange={(e) => setNewExtensionText(e.target.value.slice(0, maxLength))}
                placeholder={`Enter ${assetType.replace(/_/g, ' ')} text (max ${maxLength} chars)`}
                maxLength={maxLength}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                {newExtensionText.length}/{maxLength} characters
              </p>
            </div>
          )}

          {/* Sitelink-specific fields */}
          {assetType === 'sitelink' && (
            <>
              <div>
                <Input
                  type="url"
                  value={newSitelinkUrl}
                  onChange={(e) => setNewSitelinkUrl(e.target.value)}
                  placeholder="https://example.com/page"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Destination URL for this sitelink
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Input
                    type="text"
                    value={newSitelinkDesc1}
                    onChange={(e) => setNewSitelinkDesc1(e.target.value.slice(0, SITELINK_DESCRIPTION_LIMIT))}
                    placeholder="Description 1 (optional)"
                    maxLength={SITELINK_DESCRIPTION_LIMIT}
                    className="w-full"
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    value={newSitelinkDesc2}
                    onChange={(e) => setNewSitelinkDesc2(e.target.value.slice(0, SITELINK_DESCRIPTION_LIMIT))}
                    placeholder="Description 2 (optional)"
                    maxLength={SITELINK_DESCRIPTION_LIMIT}
                    className="w-full"
                  />
                </div>
              </div>
            </>
          )}

          {/* Structured snippet-specific fields */}
          {assetType === 'structured_snippet' && (
            <>
              <div>
                <select
                  value={newSnippetHeader}
                  onChange={(e) => setNewSnippetHeader(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  {STRUCTURED_SNIPPET_HEADERS.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Header category for this snippet
                </p>
              </div>
              <div>
                <Input
                  type="text"
                  value={newSnippetValues}
                  onChange={(e) => setNewSnippetValues(e.target.value)}
                  placeholder="Value 1, Value 2, Value 3, ... (comma-separated, min 3)"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  3-10 values required, max {maxLength} chars each
                </p>
              </div>
            </>
          )}

          {/* Action buttons - left aligned like Campaign Management */}
          <div className="flex gap-2">
            <Button
              onClick={handleAddExtension}
              disabled={isSaving || !isFormValid()}
              variant="primary"
            >
              {isSaving ? 'Adding...' : 'Add'}
            </Button>
            <Button
              onClick={resetForm}
              variant="secondary"
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Batch Action Confirmation Dialog */}
      {pendingAction && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setPendingAction(null)}
          onConfirm={async () => {
            await handleBatchStatusUpdate(pendingAction);
            setPendingAction(null);
          }}
          title={`${pendingAction.charAt(0).toUpperCase() + pendingAction.slice(1)} ${assetType.replace('_', ' ')}s`}
          message={`Are you sure you want to ${pendingAction} ${selectedAssets.size} selected ${assetType.replace('_', ' ')}${selectedAssets.size > 1 ? 's' : ''}?`}
          confirmText={pendingAction.charAt(0).toUpperCase() + pendingAction.slice(1)}
          cancelText="Cancel"
          variant={pendingAction === 'remove' ? 'danger' : pendingAction === 'pause' ? 'warning' : 'info'}
          isLoading={isUpdatingStatus}
        />
      )}
    </div>
  );
}

export default ExtensionSection;
